import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Alert, 
  CircularProgress,
  useTheme
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock as LockIcon, ArrowBack as ArrowBackIcon, Email } from '@mui/icons-material';

const ResetPassword = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { resetPassword, handlePasswordReset } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: ''
  });
  const [step, setStep] = useState(1); // 1 = request reset, 2 = reset password
  
  // Check if this is a password reset link from email
  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1)); // Remove the '#' and parse
    
    // Check for Supabase access token in the URL
    if (params.get('type') === 'recovery' && params.get('access_token')) {
      setStep(2);
      // Store the access token in session storage for later use
      sessionStorage.setItem('supabase.auth.token', params.get('access_token'));
    }
    
    // Check for access token in session storage
    const accessToken = sessionStorage.getItem('supabase.auth.token');
    if (accessToken) {
      setStep(2);
    }
    
    // Clear the hash from the URL to prevent issues with the router
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    if (!formData.email) {
      return setError('Please enter your email address');
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return setError('Please enter a valid email address');
    }

    try {
      setLoading(true);
      const { error } = await resetPassword(formData.email);
      
      if (error) throw error;
      
      setMessage('Password reset email sent. Please check your inbox.');
      setStep(1.5); // Show success message
    } catch (error) {
      console.error('Error sending reset email:', error);
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    try {
      setLoading(true);
      
      // Use handlePasswordReset from AuthContext which properly handles the password update
      const result = await handlePasswordReset(formData.password);
      
      if (result.error) throw result.error;
      
      setMessage('Your password has been updated successfully! Redirecting to login...');
      
      // Clear the access token from session storage
      sessionStorage.removeItem('supabase.auth.token');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Your password has been updated successfully. Please sign in with your new password.' 
          },
          replace: true // Replace the current entry in the history stack
        });
      }, 2000);
    } catch (error) {
      console.error('Error updating password:', error);
      
      // Provide more user-friendly error messages
      let errorMessage = 'Failed to update password. ';
      if (error.message.includes('invalid') || error.message.includes('expired')) {
        errorMessage += 'The reset link is invalid or has expired. Please request a new one.';
      } else if (error.message.includes('network')) {
        errorMessage += 'Network error. Please check your connection and try again.';
      } else {
        errorMessage += error.message || 'Please try again later.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Password reset form (step 2)
  if (step === 2) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockIcon color="primary" sx={{ fontSize: 50, mb: 1 }} />
              <Typography component="h1" variant="h5">
                Set a New Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please enter your new password below.
              </Typography>
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {message ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleUpdatePassword} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="New Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <LockIcon color="action" sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <LockIcon color="action" sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #800020 30%, #a0002a 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #600018 30%, #800020 90%)',
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Password'}
                </Button>
              </Box>
            )}
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/login')}
                sx={{ color: 'text.secondary' }}
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Request password reset form (step 1 and 1.5)
  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon color="primary" sx={{ fontSize: 50, mb: 1 }} />
            <Typography component="h1" variant="h5">
              {step === 1 ? 'Forgot Password' : 'Check Your Email'}
            </Typography>
            {step === 1 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Enter your email and we'll send you a link to reset your password.
              </Typography>
            )}
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {message ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          ) : null}
          
          {step === 1 ? (
            <Box component="form" onSubmit={handleResetPassword} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <Email color="action" sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #800020 30%, #a0002a 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #600018 30%, #800020 90%)',
                  }
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
              </Button>
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/login')}
                  sx={{ color: 'text.secondary' }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We've sent a password reset link to <strong>{formData.email}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Check your email and follow the instructions to reset your password.
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Didn't receive the email?
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleResetPassword}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  Resend Email
                </Button>
                
                <Button
                  variant="text"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, email: '' }));
                    setStep(1);
                    setMessage('');
                  }}
                  disabled={loading}
                >
                  Use Different Email
                </Button>
              </Box>
              
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/login')}
                  sx={{ color: 'text.secondary' }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
